import type { TeamTag } from '@rhythmic/db/types'
import type { UseLiveQueryReturn } from '@tanstack/vue-db'
import { useLiveQuery } from '@tanstack/vue-db'
import type { MaybeRefOrGetter } from 'vue'
import { computed, toValue } from 'vue'
import { useRhythmicLogger } from '#imports'
import { TEAM_TAG_TABLE } from '../collections'
import { useCollection } from '../composables/useCollection'

/**
 * Query teams by workspace - accepts a reactive workspace ref
 * This allows proper reactive chaining when workspace is already loaded
 */
export function useTagsByTeam(
  teamId: MaybeRefOrGetter<string | null | undefined>,
): UseLiveQueryReturn<TeamTag> | undefined {
  const logger = useRhythmicLogger('useTeamsByWorkspace')

  const teamIdValue = computed(() => toValue(teamId))

  if (!teamIdValue.value) {
    logger.debug('Skipping teams query: missing team')
    return undefined
  }

  const { collection } = useCollection<TeamTag>(TEAM_TAG_TABLE, {
    team_id: teamIdValue.value,
  })

  const query = useLiveQuery(
    (builder) =>
      builder.from({ t: collection }).orderBy(({ t }) => (t as TeamTag).tag ?? (t as TeamTag).created_at ?? '', 'asc'),
    [() => collection, () => teamIdValue.value],
  )

  return query
}
